export type ApplyStatus =
  | "pending" | "claimed" | "applying"
  | "success" | "failed" | "rolled-back" | "held"
  | "awaiting-engine-update";

export interface ApplyProvenance {
  /**
   * Who asked for this update.
   *
   * `cd` is m8t's own canary rail requesting a build it just published. It is a
   * distinct value rather than reusing `button` because the two are genuinely
   * different events, and the difference is what an operator reads when a fleet
   * event arrives: without it, a canary rail update is indistinguishable from a
   * customer's own scheduled one. The telemetry trigger enum carries the same
   * value for the same reason (m8t-labs/m8t-admin#15).
   */
  source: "button" | "policy" | "cd";
  identity: string;
  at: string;
}

export interface ApplyRequest {
  schemaVersion: 1;
  target: string;
  provenance: ApplyProvenance;
  status: ApplyStatus;
  phase: string | null;
  claimedBy: string | null;
  leaseUntil: string | null;
  attempt: number;
  breaker: "none" | "rolled-back" | "held";
  result: { appliedVersion: string | null; error: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export const APPLY_REQUEST_PK = "system";
export const APPLY_REQUEST_RK = "apply-request";
export const IN_FLIGHT: ApplyStatus[] = ["claimed", "applying"];

/**
 * How long a claiming engine holds the row before another may take it over.
 * One converge's worth.
 */
export const LEASE_MS = 30 * 60 * 1000;

/**
 * How many times an abandoned in-flight row may be taken over before the updater
 * stops rescuing it and holds. Each takeover costs a full converge, and only
 * happens after the lease expires — so without a cap, a converge that dies
 * mid-apply every time would re-roll real infrastructure forever, unattended.
 */
export const MAX_LEASE_TAKEOVERS = 2;

/**
 * The longest a HEALTHY converge can legitimately stay in-flight: the first
 * attempt plus every permitted takeover, each holding a full lease.
 *
 * This lives here, beside the values it is computed from, because two different
 * processes need it and they must not disagree. The updater job owns the
 * retrying; the gateway's reaper must wait past the end of it before concluding
 * that nobody is advancing a row. A reaper holding its own hardcoded guess at
 * this number is a control derived from a copy of its subject — it would keep
 * reading "correct" while drifting away from the behaviour it is supposed to
 * outlast, and the failure would be the worst kind: reaping a converge that was
 * still legitimately running.
 */
export const MAX_HEALTHY_IN_FLIGHT_MS = LEASE_MS * (MAX_LEASE_TAKEOVERS + 1);

export function isInFlight(status: ApplyStatus): boolean {
  return IN_FLIGHT.includes(status);
}

/** Statuses a NEW intent must never overwrite: the queue itself (pending — the
 *  updater may claim it at any moment), an engine mid-apply (claimed/applying),
 *  and the engine's own image swap (awaiting-engine-update, which sits between
 *  ticks with no lease). Terminal rows (success/failed/rolled-back/held) are
 *  overwritable — that is what a retry is. */
export const BLOCKS_NEW_INTENT: ApplyStatus[] = ["pending", "claimed", "applying", "awaiting-engine-update"];

export function blocksNewIntent(status: ApplyStatus): boolean {
  return BLOCKS_NEW_INTENT.includes(status);
}

export function newIntent(target: string, provenance: ApplyProvenance, nowIso: string): ApplyRequest {
  return {
    schemaVersion: 1, target, provenance, status: "pending", phase: null,
    claimedBy: null, leaseUntil: null, attempt: 0, breaker: "none", result: null,
    createdAt: nowIso, updatedAt: nowIso,
  };
}

export function applyRequestToEntity(r: ApplyRequest): { partitionKey: string; rowKey: string; value: string; updatedAt: string } {
  return { partitionKey: APPLY_REQUEST_PK, rowKey: APPLY_REQUEST_RK, value: JSON.stringify(r), updatedAt: r.updatedAt };
}

export function entityToApplyRequest(e: { value: string }): ApplyRequest {
  return JSON.parse(e.value) as ApplyRequest;
}
